var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import axios from "axios";
import * as E from "fp-ts/Either";
import { pipe } from "fp-ts/lib/function";
import isNil from "lodash/isNil";
import isUndefined from "lodash/isUndefined";
import { match } from "ts-pattern";
import Cookies from "universal-cookie";
import { getEtlEndpoint, getFilesEndpoint } from "../../../graphQLClient";
import { ResourceType } from "../type";
const getCookieValues = (applicationCookies) => {
    const cookies = new Cookies();
    const jwt = cookies.get(applicationCookies.jwt);
    const uuid = cookies.get(applicationCookies.uuid);
    return Object.assign(Object.assign({}, (typeof jwt === "string" ? { jwt } : {})), (typeof uuid === "string" ? { uuid } : {}));
};
const getHeaders = (applicationCookies) => {
    const { jwt, uuid } = getCookieValues(applicationCookies);
    return Object.assign(Object.assign({}, (jwt ? { Authorization: `bearer ${jwt}` } : {})), (uuid ? { uuid } : {}));
};
const getResourceType = (resourceType) => {
    return match(resourceType)
        .with(ResourceType.etl, () => getEtlEndpoint())
        .otherwise(() => getFilesEndpoint());
};
const hasProperty = (obj, prop) => {
    if (typeof obj === "object" && !isNil(obj)) {
        return prop in obj;
    }
    return false;
};
const isString = (s) => typeof s === "string";
const asString = (s) => (isString(s) ? s : JSON.stringify(s));
export const __uploaderClientRest = ({ fileName, cookies, source, name, tags, originalName, maxFileSize = 10485760, resourceType, endpoint, formData, overwrite, onLoad, onSuccess, onError, }) => {
    return (file) => __awaiter(void 0, void 0, void 0, function* () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad(file);
        if (file.size > maxFileSize) {
            return onError(new Error("File must be less than 10mb"), file);
        }
        try {
            const _f = new FormData();
            _f.append("file", file, file.name);
            _f.append("fileName", fileName);
            if (source) {
                _f.append("source", source);
            }
            if (name) {
                _f.append("name", name);
            }
            if (tags) {
                _f.append("tags", JSON.stringify(tags));
            }
            if (originalName) {
                _f.append("originalName", originalName);
            }
            if (!isUndefined(overwrite)) {
                _f.append("overwrite", JSON.stringify(overwrite));
            }
            if (formData)
                Object.keys(formData).map((c) => {
                    _f.append(c, asString(formData[c]));
                });
            const ret = yield axios.request({
                headers: getHeaders(cookies),
                url: `${getResourceType(resourceType)}${endpoint || ""}`,
                method: "POST",
                data: _f,
                validateStatus: (status) => {
                    return status >= 200 && status < 300;
                },
            });
            return onSuccess(ret.data);
        }
        catch (e) {
            return onError(e, file);
        }
    });
};
const generateBlockId = (blockString) => {
    return pipe(blockString, E.fromPredicate((blockString) => blockString.length > 64, (blockString) => blockString), E.match((blockString) => blockString, (blockString) => blockString.substring(blockString.length - 64)), (blockString) => blockString.padStart(64, "0"), btoa);
};
export const __uploaderClientRestStream = ({ fileName, cookies, source, name, tags, originalName, chunkSize = 4 * 1024 * 1024, endpoint, resourceType, formData, uploadResourceType, onLoad, onFileLoad, onSuccess, onError, onProgress, }) => {
    return (file) => __awaiter(void 0, void 0, void 0, function* () {
        onLoad === null || onLoad === void 0 ? void 0 : onLoad(file);
        const reader = new FileReader();
        let offset = 0;
        const totalChunks = Math.ceil(file.size / chunkSize);
        const uploadChunk = (data, chunkIndex) => __awaiter(void 0, void 0, void 0, function* () {
            const blockId = generateBlockId(`${file.name}-${chunkIndex}`);
            const _f = new FormData();
            _f.append("blockId", blockId);
            _f.append("fileChunk", data, file.name);
            _f.append("fileName", fileName);
            _f.append("chunkIndex", chunkIndex.toString());
            _f.append("totalChunks", totalChunks.toString());
            _f.append("uploadResourceType", uploadResourceType);
            if (source)
                _f.append("source", source);
            if (name)
                _f.append("name", name);
            if (tags)
                _f.append("tags", JSON.stringify(tags));
            if (originalName)
                _f.append("originalName", originalName);
            if (formData) {
                Object.keys(formData).map((c) => {
                    _f.append(c, asString(formData[c]));
                });
            }
            try {
                const ret = yield axios.request({
                    headers: getHeaders(cookies),
                    url: `${getResourceType(resourceType)}${endpoint || ""}`,
                    method: "POST",
                    data: _f,
                    validateStatus: (status) => {
                        return status >= 200 && status < 300;
                    },
                });
                if (hasProperty(ret.data, "progress")) {
                    onProgress === null || onProgress === void 0 ? void 0 : onProgress(ret.data.progress, file);
                }
                if (chunkIndex + 1 === totalChunks && ret.data.file) {
                    return onSuccess(Object.assign(Object.assign({}, ret.data), { file: {
                            containerName: ret.data.file.containerName,
                            name: ret.data.file.name,
                            originalName: ret.data.file.originalName,
                            size: file.size,
                            url: ret.data.file.url,
                        } }));
                }
            }
            catch (e) {
                const err = e instanceof Error ? e : new Error(String(e));
                return onError(err, file);
            }
        });
        const processNextChunk = () => {
            if (offset >= file.size)
                return;
            const chunkStart = offset;
            const chunkEnd = Math.min(file.size, chunkStart + chunkSize);
            const chunk = file.slice(chunkStart, chunkEnd);
            reader.onload = (event) => __awaiter(void 0, void 0, void 0, function* () {
                var _a;
                const data = (_a = event === null || event === void 0 ? void 0 : event.target) === null || _a === void 0 ? void 0 : _a.result;
                const chunkIndex = Math.floor(chunkStart / chunkSize);
                onFileLoad === null || onFileLoad === void 0 ? void 0 : onFileLoad({ data, size: file.size });
                yield uploadChunk(chunk, chunkIndex);
                offset += chunkSize;
                processNextChunk();
            });
            reader.readAsText(chunk);
        };
        processNextChunk();
    });
};
//# sourceMappingURL=request.rest.js.map